import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateMedicalOrganizationRequest,
  MedicalOrganization,
  UpdateMedicalOrganizationRequest,
} from '@interfaces/medicalOrganizations.interface';

import {
  useCreateMedicalOrganizationMutation,
  useUpdateMedicalOrganizationMutation,
} from '@api/medicalOrganizations.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID =
  'upsertMedicalOrganization';

interface UpsertMedicalOrganizationFormProps {
  medicalOrganization?: MedicalOrganization;
  afterSubmit?: (newMedicalOrganization: MedicalOrganization) => void;
}

export default function UpsertMedicalOrganizationForm({
  medicalOrganization,
  afterSubmit,
}: UpsertMedicalOrganizationFormProps) {
  const initialValues = {
    name: medicalOrganization?.user.name || '',
    email: medicalOrganization?.user.email || '',
    vatNumber: medicalOrganization?.vatNumber || '',
    phone: medicalOrganization?.phone || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['name'],
      uppercaseFields: ['vatNumber'],
      lowercaseFields: ['email'],
    },
  });

  const [createMedicalOrganization, { isLoading: isLoadingCreate }] =
    useCreateMedicalOrganizationMutation();
  const [updateMedicalOrganization, { isLoading: isLoadingUpdate }] =
    useUpdateMedicalOrganizationMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      let newMedicalOrganization;

      if (medicalOrganization) {
        const data: UpdateMedicalOrganizationRequest = {
          id: medicalOrganization.id,
          body: {
            name: values.name,
            email: values.email,
            vatNumber: values.vatNumber,
            phone: values.phone,
          },
        };

        newMedicalOrganization = await updateMedicalOrganization(data).unwrap();

        notifications.show({
          title: 'Organizzazione medica modificata',
          message: 'Organizzazione medica modificata con successo.',
        });
      } else {
        const data: CreateMedicalOrganizationRequest = {
          name: values.name,
          email: values.email,
          vatNumber: values.vatNumber,
          phone: values.phone,
        };

        newMedicalOrganization = await createMedicalOrganization(data).unwrap();

        notifications.show({
          title: 'Organizzazione medica creata',
          message:
            "Nuova organizzazione medica creata con successo. L'utente riceverà un'email con le istruzioni per accedere.",
        });
      }

      closeModal(UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID);

      afterSubmit?.(newMedicalOrganization);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
        <TextInput
          required
          type="email"
          label="Email"
          description={
            medicalOrganization
              ? null
              : "Verrà inviata un'email con le istruzioni per accedere"
          }
          {...form.getInputProps('email')}
        />
        <TextInput
          required
          type="vatNumber"
          label="Partita IVA"
          {...form.getInputProps('vatNumber')}
        />
        <TextInput
          required
          type="phone"
          label="Telefono"
          {...form.getInputProps('phone')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
